import React from 'react';
import { getColor } from '../../theme/colors';

// Chevron-shaped tab nav. Each tab is a pentagonal arrow shape that
// points right; tiles overlap so the sequence reads as a left→right
// flow (Connectors → Databases → Models → Analytics → UX).
//
// Active step uses the violet brand accent; inactive steps use a
// muted navy.
//
// We use CSS clip-path for the chevron shape so we avoid any image
// assets or SVG packs.

const NOTCH = 14; // px — width of the notch/arrow point

function clipFor(position) {
  // The three positions all share the same right-pointing arrow on the
  // right edge. Only the first tab has a straight left edge; the rest
  // notch inward on the left so they fit snugly against the previous
  // tab's arrow point.
  if (position === 'first') {
    return `polygon(0 0, calc(100% - ${NOTCH}px) 0, 100% 50%, calc(100% - ${NOTCH}px) 100%, 0 100%)`;
  }
  return `polygon(0 0, calc(100% - ${NOTCH}px) 0, 100% 50%, calc(100% - ${NOTCH}px) 100%, 0 100%, ${NOTCH}px 50%)`;
}

export default function ArchitectureTabNav({ tabs, activeTab, onTabChange }) {
  const accent = getColor('violet');
  const inactiveBg = getColor('darkblue');
  return (
    <div className="flex items-stretch w-full mb-2">
      {tabs.map((tab, i) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        const isFirst = i === 0;
        const clipPath = clipFor(isFirst ? 'first' : 'middle');

        // Active = violet brand fill; inactive = soft navy fill that
        // contrasts cleanly against the white page background and the
        // adjacent active tab. Hover classes are literal so Tailwind's
        // JIT picks them up (theme interpolation in hover: won't work).
        const toneClasses = isActive
          ? `${accent.bgSolid} text-white`
          : `${inactiveBg.bgLight} ${inactiveBg.text} hover:bg-darkblue-600 hover:text-white`;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`relative flex items-center justify-center text-sm font-medium transition-colors ${toneClasses}`}
            style={{
              clipPath,
              // Extra horizontal padding accommodates the arrow point and
              // the notch on either side so the label has room.
              padding: `10px ${NOTCH + 18}px 10px ${isFirst ? 16 : NOTCH + 14}px`,
              marginLeft: isFirst ? 0 : -NOTCH,
              flex: '1 1 0',
              minWidth: 0,
            }}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className="flex items-center gap-2 truncate">
              {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
              <span className="truncate">{tab.name}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
